// Helpers for sending events into an OpenAI Realtime websocket.
//
// All functions are no-ops when the socket is not OPEN, so callers can
// fire-and-forget without having to guard each call site.
function isOpen(ws) {
    return !!ws && ws.readyState === WebSocket.OPEN;
}
// Push a text item into the conversation. Safe to call any time —
// non-interrupting; OpenAI just appends it to the conversation history
// and considers it on the next response. Use this for system
// instructions delivered mid-session (time warnings, time-over).
export function appendConversationItem(ws, role, text) {
    if (!isOpen(ws))
        return;
    ws.send(JSON.stringify({
        type: 'conversation.item.create',
        item: {
            type: 'message',
            role,
            content: [{ type: 'input_text', text }],
        },
    }));
}
// Trigger an assistant response. Should only be called when no response
// is currently active *and* the candidate isn't speaking — otherwise
// OpenAI will conflict or talk over the user. Callers should consult
// the speaking flags before invoking.
//
// The backend already attached the session configuration when it minted
// the ephemeral key, so we intentionally do not override modalities or
// audio format here. This keeps the client aligned with the current
// session schema and lets the server-side session config stay the
// source of truth.
export function createResponse(ws) {
    if (!isOpen(ws))
        return;
    ws.send(JSON.stringify({ type: 'response.create' }));
}
// Cancel the current in-progress response. Used when the candidate
// starts speaking and we want the bot to stop.
export function cancelResponse(ws) {
    if (!isOpen(ws))
        return;
    ws.send(JSON.stringify({ type: 'response.cancel' }));
}
// Append a base64 PCM16 chunk of mic audio to the input buffer.
export function appendInputAudio(ws, base64Pcm16) {
    if (!isOpen(ws))
        return;
    ws.send(JSON.stringify({ type: 'input_audio_buffer.append', audio: base64Pcm16 }));
}
// Clear any audio currently sitting in OpenAI's input buffer. Used at
// submission time so the model doesn't try to process accumulated mic
// silence frames (which can delay or confuse the explicit
// ``response.create`` we're about to send).
export function clearInputAudioBuffer(ws) {
    if (!isOpen(ws))
        return;
    ws.send(JSON.stringify({ type: 'input_audio_buffer.clear' }));
}
// Build the OpenAI Realtime websocket URL using the ephemeral session
// token returned by the backend's ``session_create`` payload.
//
// Note: session configuration (turn detection, modalities, transcription,
// tools, etc.) is owned by the backend when it creates the client
// secret. The frontend does NOT send ``session.update`` — it just
// connects with the ephemeral key and the server-side config is already
// active. The ``model`` query param MUST match the model the session was
// created with, otherwise OpenAI rejects the connection.
export function openRealtimeWebSocket(clientSecret, model = 'gpt-realtime-mini') {
    return new WebSocket(`wss://api.openai.com/v1/realtime?model=${encodeURIComponent(model)}`, [
        'realtime',
        `openai-insecure-api-key.${clientSecret}`,
    ]);
}
